<template>
  <div class="charge-field">
    <h1 class="title">⚡ Движение заряда в электрическом поле</h1>
    <p class="subtitle">
      Интерактивная симуляция электростатического поля точечных зарядов
    </p>

    <div class="layout">
      <!-- Canvas -->
      <div class="canvas-wrap">
        <canvas
          ref="canvas"
          :width="W"
          :height="H"
          @mousedown="onMouseDown"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
          @dblclick="onDoubleClick"
        />
        <div class="canvas-hint">
          ЛКМ — перетащить заряд · Клик по холсту — поставить тестовый заряд · Двойной клик по заряду — удалить
        </div>
      </div>

      <!-- Панель управления -->
      <div class="side-panel">
        <!-- Источники поля -->
        <div class="panel-block">
          <h3>Источники поля</h3>
          <div class="btn-row">
            <button class="btn-add pos" @click="addCharge(+chargeMag)">+ Добавить +Q</button>
            <button class="btn-add neg" @click="addCharge(-chargeMag)">− Добавить −Q</button>
          </div>

          <div class="control-group">
            <label>Величина |Q| нового заряда</label>
            <input v-model.number="chargeMag" type="range" min="1" max="10" step="1" class="slider" />
            <span class="value">{{ chargeMag }} нКл</span>
          </div>

          <div class="charge-list">
            <div
              v-for="(c, i) in charges"
              :key="c.id"
              class="charge-item"
              :class="{ pos: c.q > 0, neg: c.q < 0 }"
            >
              <span>Заряд {{ i + 1 }}: {{ c.q > 0 ? '+' : '' }}{{ c.q }} нКл</span>
              <button class="btn-del" @click="removeCharge(i)">×</button>
            </div>
          </div>
        </div>

        <!-- Тестовый заряд -->
        <div class="panel-block">
          <h3>Тестовый заряд</h3>
          <div class="btn-row toggle">
            <button :class="['toggle-btn', { active: testQ > 0 }]" @click="testQ = +1">+q</button>
            <button :class="['toggle-btn', { active: testQ < 0 }]" @click="testQ = -1">−q</button>
          </div>

          <div class="control-group">
            <label>Начальная скорость vₓ, м/с</label>
            <input v-model.number="vx0" type="range" min="-100" max="100" step="5" class="slider" />
            <span class="value">{{ vx0 }} м/с</span>
          </div>

          <div class="control-group">
            <label>Начальная скорость vᵧ, м/с</label>
            <input v-model.number="vy0" type="range" min="-100" max="100" step="5" class="slider" />
            <span class="value">{{ vy0 }} м/с</span>
          </div>

          <div class="btn-row">
            <button class="btn-run" @click="toggleRun">
              {{ running ? '⏸ Стоп' : '▶ Пуск' }}
            </button>
            <button class="btn-reset" @click="resetTest">↺ Сбросить</button>
          </div>
        </div>

        <!-- Отображение -->
        <div class="panel-block">
          <h3>Визуализация</h3>
          <label class="check-row">
            <input v-model="showFieldLines" type="checkbox" />
            <span>Линии напряжённости</span>
          </label>
          <label class="check-row">
            <input v-model="showVectors" type="checkbox" />
            <span>Векторы поля (сетка)</span>
          </label>
          <label class="check-row">
            <input v-model="showTrail" type="checkbox" />
            <span>Траектория</span>
          </label>
        </div>

        <!-- Информация -->
        <div class="panel-block info">
          <h3>Справка</h3>
          <p><strong>E</strong> = Σ k·Qᵢ·r̂ / r² — напряжённость поля</p>
          <p><strong>F</strong> = q·E — сила на тестовый заряд</p>
          <p><strong>a</strong> = F/m — ускорение</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

/* ==================== КОНФИГУРАЦИЯ ==================== */
const W = 900
const H = 600
const k = 8000        // масштабный коэффициент кулоновского поля
const dt = 0.25       // шаг интегрирования
const Q_RADIUS = 18   // радиус отрисовки источника
const TEST_RADIUS = 8 // радиус тестового заряда

/* ==================== СОСТОЯНИЕ ==================== */
const canvas = ref(null)
let ctx = null

const charges = ref([])
let nextId = 1

const testQ = ref(+1)
const vx0 = ref(30)
const vy0 = ref(0)
const chargeMag = ref(3)

const running = ref(false)
const showFieldLines = ref(true)
const showVectors = ref(false)
const showTrail = ref(true)

const testCharge = ref(null) // {x, y, vx, vy, trail: []}

let animId = null
let dragIdx = -1
let dragOffsetX = 0
let dragOffsetY = 0

/* ==================== ИНИЦИАЛИЗАЦИЯ ==================== */
onMounted(() => {
  ctx = canvas.value.getContext('2d')
  // Два демо-заряда: + и −
  charges.value = [
    { id: nextId++, x: W * 0.35, y: H / 2, q: +5 },
    { id: nextId++, x: W * 0.65, y: H / 2, q: -5 },
  ]
  draw()
})

onUnmounted(() => {
  if (animId) cancelAnimationFrame(animId)
})

/* ==================== ФИЗИКА ==================== */
function getField(x, y) {
  let Ex = 0, Ey = 0
  for (const c of charges.value) {
    const dx = x - c.x
    const dy = y - c.y
    const r2 = dx * dx + dy * dy
    if (r2 < 1) continue
    const r = Math.sqrt(r2)
    const E = (k * c.q) / r2
    Ex += E * dx / r
    Ey += E * dy / r
  }
  return { Ex, Ey }
}

function fieldMagnitude(Ex, Ey) {
  return Math.sqrt(Ex * Ex + Ey * Ey)
}

/* ==================== УПРАВЛЕНИЕ ==================== */
function addCharge(q) {
  charges.value.push({
    id: nextId++,
    x: W / 2 + (Math.random() - 0.5) * 200,
    y: H / 2 + (Math.random() - 0.5) * 200,
    q
  })
  draw()
}

function removeCharge(idx) {
  charges.value.splice(idx, 1)
  draw()
}

function resetTest() {
  testCharge.value = null
  running.value = false
  draw()
}

function toggleRun() {
  if (!testCharge.value) return
  running.value = !running.value
  if (running.value) loop()
}

/* ==================== МЫШЬ ==================== */
function findCharge(x, y) {
  for (let i = charges.value.length - 1; i >= 0; i--) {
    const c = charges.value[i]
    const dx = x - c.x, dy = y - c.y
    if (dx * dx + dy * dy <= Q_RADIUS * Q_RADIUS + 10) return i
  }
  return -1
}

function onMouseDown(e) {
  const rect = canvas.value.getBoundingClientRect()
  const mx = (e.clientX - rect.left) * (W / rect.width)
  const my = (e.clientY - rect.top) * (H / rect.height)

  const idx = findCharge(mx, my)
  if (idx >= 0) {
    dragIdx = idx
    dragOffsetX = mx - charges.value[idx].x
    dragOffsetY = my - charges.value[idx].y
  } else {
    // Поставить тестовый заряд
    spawnTest(mx, my)
  }
}

function onMouseMove(e) {
  if (dragIdx < 0) return
  const rect = canvas.value.getBoundingClientRect()
  const mx = (e.clientX - rect.left) * (W / rect.width)
  const my = (e.clientY - rect.top) * (H / rect.height)

  charges.value[dragIdx].x = Math.max(Q_RADIUS, Math.min(W - Q_RADIUS, mx - dragOffsetX))
  charges.value[dragIdx].y = Math.max(Q_RADIUS, Math.min(H - Q_RADIUS, my - dragOffsetY))
  draw()
}

function onMouseUp() {
  dragIdx = -1
}

function onDoubleClick(e) {
  const rect = canvas.value.getBoundingClientRect()
  const mx = (e.clientX - rect.left) * (W / rect.width)
  const my = (e.clientY - rect.top) * (H / rect.height)
  const idx = findCharge(mx, my)
  if (idx >= 0) removeCharge(idx)
}

function spawnTest(x, y) {
  testCharge.value = {
    x, y,
    vx: vx0.value,
    vy: vy0.value,
    trail: [{ x, y }]
  }
  running.value = true
  loop()
}

/* ==================== АНИМАЦИЯ ==================== */
function loop() {
  if (!running.value) return
  updatePhysics()
  draw()
  animId = requestAnimationFrame(loop)
}

function updatePhysics() {
  const t = testCharge.value
  if (!t) return

  const { Ex, Ey } = getField(t.x, t.y)
  const ax = (testQ.value * Ex) / 1 // m = 1
  const ay = (testQ.value * Ey) / 1

  t.vx += ax * dt
  t.vy += ay * dt
  t.x += t.vx * dt
  t.y += t.vy * dt

  t.trail.push({ x: t.x, y: t.y })
  if (t.trail.length > 1200) t.trail.shift()

  // Проверка столкновения с источниками
  for (const c of charges.value) {
    const dx = t.x - c.x, dy = t.y - c.y
    if (dx * dx + dy * dy < (Q_RADIUS + TEST_RADIUS) ** 2) {
      running.value = false
      return
    }
  }

  // Проверка границ
  if (t.x < -200 || t.x > W + 200 || t.y < -200 || t.y > H + 200) {
    running.value = false
  }
}

/* ==================== ОТРИСОВКА ==================== */
function draw() {
  if (!ctx) return
  ctx.clearRect(0, 0, W, H)
  ctx.fillStyle = '#0b0f19'
  ctx.fillRect(0, 0, W, H)

  if (showFieldLines.value) drawFieldLines()
  if (showVectors.value) drawVectors()
  if (showTrail.value && testCharge.value) drawTrail()
  drawCharges()
  if (testCharge.value) drawTestCharge()
}

function drawFieldLines() {
  ctx.lineWidth = 1.2
  const step = 6
  const maxSteps = 300

  for (const c of charges.value) {
    if (c.q === 0) continue
    const startCount = Math.max(12, Math.round(Math.abs(c.q) * 3))
    for (let i = 0; i < startCount; i++) {
      const angle = (Math.PI * 2 * i) / startCount
      let x = c.x + Math.cos(angle) * (Q_RADIUS + 4)
      let y = c.y + Math.sin(angle) * (Q_RADIUS + 4)

      ctx.beginPath()
      ctx.moveTo(x, y)
      let dead = false
      for (let s = 0; s < maxSteps && !dead; s++) {
        const { Ex, Ey } = getField(x, y)
        const E = fieldMagnitude(Ex, Ey)
        if (E < 0.01) break
        const dir = c.q > 0 ? 1 : -1
        x += (Ex / E) * step * dir
        y += (Ey / E) * step * dir
        ctx.lineTo(x, y)

        // Остановка у противоположного заряда
        for (const other of charges.value) {
          if (other.id === c.id) continue
          const dx = x - other.x, dy = y - other.y
          if (dx * dx + dy * dy < (Q_RADIUS + 6) ** 2 && other.q * c.q < 0) {
            dead = true
            break
          }
        }
        if (x < -50 || x > W + 50 || y < -50 || y > H + 50) dead = true
      }
      ctx.strokeStyle = c.q > 0 ? 'rgba(255,80,80,0.35)' : 'rgba(80,140,255,0.35)'
      ctx.stroke()
    }
  }
}

function drawVectors() {
  const spacing = 50
  for (let x = spacing / 2; x < W; x += spacing) {
    for (let y = spacing / 2; y < H; y += spacing) {
      const { Ex, Ey } = getField(x, y)
      const E = fieldMagnitude(Ex, Ey)
      if (E < 0.1) continue
      const maxLen = 22
      const len = Math.min(E * 0.8, maxLen)
      const nx = Ex / E, ny = Ey / E
      drawArrow(x, y, x + nx * len, y + ny * len, E)
    }
  }
}

function drawArrow(x1, y1, x2, y2, E) {
  const head = 4
  const angle = Math.atan2(y2 - y1, x2 - x1)
  const intensity = Math.min(E / 300, 1)
  const r = Math.round(80 + intensity * 120)
  const g = Math.round(140 + intensity * 80)
  const b = Math.round(255 - intensity * 80)
  ctx.strokeStyle = `rgb(${r},${g},${b})`
  ctx.fillStyle = `rgb(${r},${g},${b})`
  ctx.lineWidth = 1.2

  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6))
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6))
  ctx.closePath()
  ctx.fill()
}

function drawTrail() {
  const t = testCharge.value
  if (!t || t.trail.length < 2) return
  ctx.beginPath()
  ctx.moveTo(t.trail[0].x, t.trail[0].y)
  for (let i = 1; i < t.trail.length; i++) {
    ctx.lineTo(t.trail[i].x, t.trail[i].y)
  }
  ctx.strokeStyle = testQ.value > 0 ? 'rgba(255,220,80,0.7)' : 'rgba(80,220,255,0.7)'
  ctx.lineWidth = 2.5
  ctx.stroke()
}

function drawCharges() {
  for (const c of charges.value) {
    // Свечение
    const grad = ctx.createRadialGradient(c.x, c.y, Q_RADIUS * 0.3, c.x, c.y, Q_RADIUS * 2)
    if (c.q > 0) {
      grad.addColorStop(0, 'rgba(255,80,80,0.9)')
      grad.addColorStop(1, 'rgba(255,80,80,0)')
    } else {
      grad.addColorStop(0, 'rgba(80,140,255,0.9)')
      grad.addColorStop(1, 'rgba(80,140,255,0)')
    }
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(c.x, c.y, Q_RADIUS * 2, 0, Math.PI * 2)
    ctx.fill()

    // Ядро
    ctx.fillStyle = c.q > 0 ? '#ff5050' : '#508cff'
    ctx.beginPath()
    ctx.arc(c.x, c.y, Q_RADIUS, 0, Math.PI * 2)
    ctx.fill()

    // Обводка
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2
    ctx.stroke()

    // Знак
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 16px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(c.q > 0 ? '+' : '−', c.x, c.y)

    // Подпись |Q|
    ctx.fillStyle = '#aaa'
    ctx.font = '11px sans-serif'
    ctx.fillText(`|Q|=${Math.abs(c.q)}`, c.x, c.y + Q_RADIUS + 14)
  }
}

function drawTestCharge() {
  const t = testCharge.value
  if (!t) return

  // Свечение
  const grad = ctx.createRadialGradient(t.x, t.y, TEST_RADIUS, t.x, t.y, TEST_RADIUS * 4)
  if (testQ.value > 0) {
    grad.addColorStop(0, 'rgba(255,220,80,0.9)')
    grad.addColorStop(1, 'rgba(255,220,80,0)')
  } else {
    grad.addColorStop(0, 'rgba(80,220,255,0.9)')
    grad.addColorStop(1, 'rgba(80,220,255,0)')
  }
  ctx.fillStyle = grad
  ctx.beginPath()
  ctx.arc(t.x, t.y, TEST_RADIUS * 4, 0, Math.PI * 2)
  ctx.fill()

  // Точка
  ctx.fillStyle = testQ.value > 0 ? '#ffdc50' : '#50dcff'
  ctx.beginPath()
  ctx.arc(t.x, t.y, TEST_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 1.5
  ctx.stroke()
}

watch([showFieldLines, showVectors, showTrail], draw)
</script>

<style scoped>
.charge-field {
  max-width: 1300px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #c9d1d9;
}

.title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #58a6ff;
}

.subtitle {
  font-size: 13px;
  color: #8b949e;
  margin: 0 0 18px;
}

.layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
}

@media (max-width: 1000px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

/* Canvas */
.canvas-wrap {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #30363d;
  background: #0b0f19;
}

canvas {
  display: block;
  width: 100%;
  height: auto;
  cursor: crosshair;
}

.canvas-hint {
  padding: 8px 12px;
  font-size: 11px;
  color: #6e7681;
  text-align: center;
  border-top: 1px solid #21262d;
}

/* Панель */
.side-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-block {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 10px;
  padding: 14px;
}

.panel-block h3 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #f0f6fc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #30363d;
  padding-bottom: 8px;
}

.btn-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.btn-row.toggle {
  margin-bottom: 10px;
}

.btn-add {
  flex: 1;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-add.pos { background: #c62828; }
.btn-add.pos:hover { background: #d32f2f; }
.btn-add.neg { background: #1565c0; }
.btn-add.neg:hover { background: #1976d2; }

.toggle-btn {
  flex: 1;
  padding: 8px;
  border: 1px solid #30363d;
  border-radius: 6px;
  background: #0d1117;
  color: #8b949e;
  font-size: 13px;
  cursor: pointer;
}

.toggle-btn.active {
  background: #1f6feb;
  color: #fff;
  border-color: #1f6feb;
}

.control-group {
  margin-bottom: 12px;
}

.control-group label {
  display: block;
  font-size: 11px;
  color: #8b949e;
  margin-bottom: 4px;
}

.slider {
  width: 100%;
  margin-bottom: 2px;
}

.value {
  font-size: 12px;
  color: #c9d1d9;
  font-variant-numeric: tabular-nums;
}

.charge-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 160px;
  overflow-y: auto;
}

.charge-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #30363d;
}

.charge-item.pos { background: rgba(198,40,40,0.1); border-color: rgba(198,40,40,0.3); }
.charge-item.neg { background: rgba(21,101,192,0.1); border-color: rgba(21,101,192,0.3); }

.btn-del {
  background: transparent;
  border: none;
  color: #f85149;
  font-size: 16px;
  cursor: pointer;
  line-height: 1;
}

.btn-run, .btn-reset {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-run { background: #238636; color: #fff; }
.btn-run:hover { background: #2ea043; }
.btn-reset { background: #21262d; color: #c9d1d9; border: 1px solid #30363d; }
.btn-reset:hover { background: #30363d; }

.check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  cursor: pointer;
}

.check-row input {
  accent-color: #58a6ff;
}

.panel-block.info p {
  font-size: 12px;
  color: #8b949e;
  margin: 4px 0;
  line-height: 1.5;
}

.panel-block.info strong {
  color: #c9d1d9;
}
</style>
